import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { db } from '../services/firebase';
import { UserRole } from '../types';
import { subscribeBoard, matchesOrder, BoardCard } from '../services/paymentBoard';
import { subscribeCachedEmails } from '../services/emailCache';
import { USE_SUPABASE, msFromISO0 } from '../services/supabaseClient';
import { subscribeTableHub } from '../services/tableHub';
import { FEATURES } from '../services/features';
import { subscribeBoardDirectory } from '../services/live/boardDirectory';
import { isSuperTier } from '../services/roles';

// CustomerReplyAlerts — pings the dispatcher HANDLING an order when the customer
// replies, by email OR by SMS, on a board card they own (supervisors: any
// still-unowned order too). The main NotificationHandler only watches
// delivery-order `conversations` (customer channel = drivers only) and never the
// email cache, so the mail-order desk got no alert when a customer wrote back —
// exactly the gap Umer flagged ("keep an eye on notifications").
//
// Deliberately SEPARATE from NotificationHandler so it can't destabilize that
// tuned delivery-order engine, and it only watches `card_<id>` SMS threads + the
// emails cache — surfaces that engine never touches, so there is no double-fire.
// Alerts = in-app toast + a short sound + a browser notification (when the user
// has granted permission). Rendered once, near the app root; returns null.

// Short attention beep — same 880 Hz tone the notification engine uses.
function beep() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.value = 0.25;
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.stop(ctx.currentTime + 0.3);
  } catch { /* silent — audio can be blocked before a user gesture */ }
}

// Business + internal test addresses — a message FROM one of these is us, not
// the customer.
const OURS = /uperweed|umerakram|umer\.claude/i;

export const CustomerReplyAlerts: React.FC = () => {
  const { currentUser, showToast } = useApp();
  const navigate = useNavigate();
  const [cards, setCards] = useState<BoardCard[]>([]);

  const isSupervisor =
    currentUser?.role === UserRole.ADMIN || isSuperTier(currentUser?.role);
  const isDeskRole = isSupervisor || currentUser?.role === UserRole.DISPATCH;

  // Only alert on things that arrive AFTER mount, and only once each.
  const startRef = useRef<number>(Date.now());
  const seenRef = useRef<Set<string>>(new Set());

  // Follow the board to know which cards this user should be alerted about.
  useEffect(() => {
    if (!isDeskRole) return;
    if (USE_SUPABASE && FEATURES.liveV2Board) {
      // Live v2: the alerts read id / order number / customer name / owner of
      // the live cards — the thin list shared with NotificationHandler and the
      // Messages page, not a full-row whole-table binding of its own.
      return subscribeBoardDirectory(currentUser?.role, (list) => setCards(list.filter((c) => !c.archived)));
    }
    return subscribeBoard(setCards, () => {}, { activeOnly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeskRole]);

  // Desk roles rely on these alerts — prompt for browser-notification permission
  // so the clickable pop-up (not just the sound) can fire.
  useEffect(() => {
    if (!isDeskRole) return;
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, [isDeskRole]);

  // A card's owner is alerted about it; supervisors also cover still-unowned
  // orders (they watch the shared queue).
  const alertable = useMemo(() => {
    if (!currentUser) return [] as BoardCard[];
    return cards.filter((c) => (c.assignedTo ? c.assignedTo === currentUser.id : isSupervisor));
  }, [cards, currentUser?.id, isSupervisor]);
  const alertableRef = useRef(alertable);
  useEffect(() => { alertableRef.current = alertable; }, [alertable]);

  // One alert: sound + in-app toast + browser notification (if permitted).
  const fire = (title: string, body: string, go: () => void) => {
    beep();
    showToast(body ? `${title} — ${body}` : title, 'info');
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        const n = new Notification(title, { body, tag: title });
        n.onclick = () => { window.focus(); go(); n.close(); };
      }
    } catch { /* silent */ }
  };

  // ── EMAIL: a fresh inbound customer email matching an alertable card ──
  useEffect(() => {
    if (!isDeskRole) return;
    return subscribeCachedEmails((emails) => {
      for (const em of emails) {
        if (em.folder !== 'inbox') continue;
        if (!em.date || em.date <= startRef.current) continue; // only mail since mount
        if (OURS.test(em.from || '')) continue; // our own mail, not the customer
        const key = `mail:${em.folder}-${em.uid}`;
        if (seenRef.current.has(key)) continue;
        const card = alertableRef.current.find((c) => matchesOrder(em, c.orderNumber));
        if (!card) continue;
        seenRef.current.add(key);
        fire(
          `📧 ${card.customerName || 'Customer'} replied`,
          `#${card.orderNumber} · ${em.subject || 'New email'}`,
          () => navigate(`/payment_approval?card=${card.id}`)
        );
      }
    }, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeskRole]);

  // ── SMS: a fresh inbound customer text on an alertable card thread ──
  // (Re)subscribe to the card conversation docs whenever the alertable set
  // changes. Capped so a huge board can't open unbounded listeners.
  const alertKey = alertable.map((c) => c.id).sort().join(',');
  useEffect(() => {
    if (USE_SUPABASE) {
      if (!isDeskRole) return;
      const ids = alertable.map((c) => c.id).slice(0, 40);
      if (ids.length === 0) return;
      const byId = new Map<string, BoardCard>(alertable.map((c) => [c.id, c] as [string, BoardCard]));
      const convIds = new Set(ids.map((id) => `card_${id}`));
      // `messages` INSERTs, on the tab's shared binding (services/tableHub —
      // NotificationHandler rides the same channel), replace the per-card doc
      // listeners — the payload carries the full new row, so the same alert
      // conditions run per message (customer channel, digit sender, post-mount).
      const leave = subscribeTableHub('messages', {
        events: ['INSERT'],
        onChange: (payload: any) => {
          const row = payload?.new;
          if (!row || !convIds.has(row.conversation_id)) return;
          if ((row.channel || 'customer') !== 'customer') return;
          const ts = msFromISO0(row.created_at);
          const sender = row.sender_id;
          if (!ts || ts <= startRef.current) return; // only texts since mount
          // Inbound customer texts store a phone number as senderId; a dispatcher's
          // uid is alphanumeric, so a pure-digit sender means "the customer".
          if (!sender || !/^\d{7,}$/.test(String(sender))) return;
          const key = `sms:${row.conversation_id}:${ts}`;
          if (seenRef.current.has(key)) return;
          seenRef.current.add(key);
          const cardId = String(row.conversation_id).replace(/^card_/, '');
          const card = byId.get(cardId);
          fire(
            `📱 ${card?.customerName || 'Customer'} texted`,
            `#${card?.orderNumber || ''} · ${row.body || ''}`,
            () => navigate(`/messages?order=card_${cardId}&channel=customer&name=${encodeURIComponent(card?.customerName || '')}`)
          );
        },
      });
      return () => { leave(); };
    }
    if (!isDeskRole || !db) return;
    const ids = alertable.map((c) => c.id).slice(0, 40);
    const byId = new Map<string, BoardCard>(alertable.map((c) => [c.id, c] as [string, BoardCard]));
    const unsubs: Array<() => void> = [];
    for (const id of ids) {
      const convId = `card_${id}`;
      const unsub = db.collection('conversations').doc(convId).onSnapshot((snap) => {
        const data = snap.data();
        if (!data) return;
        const ts = (data.lastMessagePerChannel && data.lastMessagePerChannel.customer) || data.lastMessageAt || 0;
        const sender = data.lastSenderPerChannel && data.lastSenderPerChannel.customer;
        if (!ts || ts <= startRef.current) return; // only texts since mount
        // Inbound customer texts store a phone number as senderId; a dispatcher's
        // uid is alphanumeric, so a pure-digit sender means "the customer".
        if (!sender || !/^\d{7,}$/.test(String(sender))) return;
        const key = `sms:${convId}:${ts}`;
        if (seenRef.current.has(key)) return;
        seenRef.current.add(key);
        const card = byId.get(id);
        fire(
          `📱 ${card?.customerName || 'Customer'} texted`,
          `#${card?.orderNumber || ''} · ${data.lastMessageBody || ''}`,
          () => navigate(`/messages?order=card_${id}&channel=customer&name=${encodeURIComponent(card?.customerName || '')}`)
        );
      }, () => {});
      unsubs.push(unsub);
    }
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertKey, isDeskRole]);

  return null;
};
